import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = '/api';
const SESSION_DURATION_MS = 30 * 60 * 1000; // 30 minutes

export function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpiry, setSessionExpiry] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  const clearSession = useCallback(() => {
    localStorage.removeItem('sessionExpiry');
    setSessionExpiry(null);
  }, []);

  const startSession = useCallback(() => {
    const expiry = Date.now() + SESSION_DURATION_MS;
    localStorage.setItem('sessionExpiry', String(expiry));
    setSessionExpiry(expiry);
    setSessionExpired(false);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      const stored = localStorage.getItem('sessionExpiry');
      if (stored && Number(stored) > Date.now()) {
        setSessionExpiry(Number(stored));
      } else {
        startSession();
      }
    } catch {
      setUser(false);
      clearSession();
    } finally {
      setLoading(false);
    }
  }, [startSession, clearSession]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(data);
    startSession();
    return data;
  };

  const register = async (email, password, name, extras = {}) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name, ...extras }, { withCredentials: true });
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
    clearSession();
    setSessionExpired(false);
  };

  const handleSessionExpired = useCallback(() => {
    setSessionExpired(true);
    clearSession();
  }, [clearSession]);

  const refreshToken = async () => {
    try {
      await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      await checkAuth();
    } catch {
      setUser(false);
      clearSession();
    }
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout, refreshToken,
      isAdmin: user?.role === 'admin',
      canViewOverviews: user?.can_view_overviews || user?.role === 'admin',
      canWriteBlog: user?.can_write_blog || user?.role === 'admin',
      canManageIt: user?.can_manage_it || user?.role === 'admin',
      sessionExpiry,
      sessionExpired,
      handleSessionExpired,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
